var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g = Object.create((typeof Iterator === "function" ? Iterator : Object).prototype);
    return g.next = verb(0), g["throw"] = verb(1), g["return"] = verb(2), typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useHistory } from "react-router-dom";
import { useIntl } from "react-intl";
import { useForm, useFieldArray, } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useOkapiKy } from "@folio/stripes/core";
import { createLegalPersonSchema } from "../utils/validationUtils";
import { useModal } from "../utils/useModal";
import { buildPersonInvalidSubmitContent } from "../utils/personInvalidSubmit";
import { LegalPersonStatusEnum, LegalPersonLegalFormEnum, } from "../types/Api";
import { navigation } from "../components/navigation";
import { createLegalPerson, fetchLegalPersonById, updateLegalPerson, } from "../api/legalPersonService";
import { fetchCurrentUser } from "../api/userService";
import { useScrollToError } from "./useScrollToError";
import { useAuthoritySendErrorCallout } from "./useAuthoritySendErrorCallout";
export var useLegalPersonForm = function (_a) {
    var initialValues = _a.initialValues, _b = _a.isEdit, isEdit = _b === void 0 ? false : _b;
    var formatMessage = useIntl().formatMessage;
    var history = useHistory();
    var ky = useOkapiKy();
    var queryClient = useQueryClient();
    var _c = useModal(), isOpen = _c.isOpen, message = _c.message, openModal = _c.openModal, closeModal = _c.closeModal;
    var _d = useAuthoritySendErrorCallout(), notifySendError = _d.notifySendError, notifySendStatusUnknown = _d.notifySendStatusUnknown;
    var currentUser = useQuery(["currentUser"], function () { return fetchCurrentUser(ky); }, { staleTime: Infinity }).data;
    var form = useForm({
        resolver: zodResolver(createLegalPersonSchema(formatMessage)),
        shouldFocusError: false,
        defaultValues: initialValues || {
            name: "",
            residence: "",
            biographicalNote: "",
            localOnly: false,
            identifiers: [],
            legalAlternativeNames: [],
        },
        mode: "onBlur",
    });
    var identifierFields = useFieldArray({
        control: form.control,
        name: "identifiers",
    });
    var alternativeNameFields = useFieldArray({
        control: form.control,
        name: "legalAlternativeNames",
    });
    var errors = form.formState.errors;
    useScrollToError(errors);
    // The update returns no body: re-read the person to learn whether sending it
    // to NK authorities failed. The save already succeeded, so a failed re-read
    // is reported as an unknown send status and the submitted data (without its
    // stale send error) is used instead of reporting a save error.
    var reReadAfterUpdate = function (data) { return __awaiter(void 0, void 0, void 0, function () {
        var withoutSendError, _a;
        return __generator(this, function (_b) {
            switch (_b.label) {
                case 0:
                    withoutSendError = __assign(__assign({}, data), { authoritySendError: undefined, authoritySendErrorAt: undefined });
                    if (!data.id) {
                        return [2 /*return*/, withoutSendError];
                    }
                    _b.label = 1;
                case 1:
                    _b.trys.push([1, 3, , 4]);
                    return [4 /*yield*/, fetchLegalPersonById(ky, data.id)];
                case 2: return [2 /*return*/, _b.sent()];
                case 3:
                    _a = _b.sent();
                    notifySendStatusUnknown();
                    return [2 /*return*/, withoutSendError];
                case 4: return [2 /*return*/];
            }
        });
    }); };
    var saveMutation = useMutation(function (data) { return __awaiter(void 0, void 0, void 0, function () {
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    if (!isEdit) return [3 /*break*/, 2];
                    return [4 /*yield*/, updateLegalPerson(ky, data)];
                case 1:
                    _a.sent();
                    return [2 /*return*/, reReadAfterUpdate(data)];
                case 2: return [2 /*return*/, createLegalPerson(ky, data)];
            }
        });
    }); }, {
        onSuccess: function (saved) {
            var _a;
            notifySendError(saved);
            queryClient.invalidateQueries(["legal-persons"]);
            if (saved === null || saved === void 0 ? void 0 : saved.id) {
                queryClient.invalidateQueries(["legalPerson", saved.id]);
            }
            history.push(((_a = navigation.legalPersons) === null || _a === void 0 ? void 0 : _a.path) || "/bragency/legal-persons");
        },
        onError: function () {
            var errorKey = isEdit
                ? "ui-br-agency.legal-person.error.update"
                : "ui-br-agency.legal-person.error.submit";
            openModal(formatMessage({ id: errorKey }));
        },
    });
    var handleFormSubmit = function (data) { return __awaiter(void 0, void 0, void 0, function () {
        var formattedData, proposerId;
        var _a, _b, _c;
        return __generator(this, function (_d) {
            if (isEdit) {
                formattedData = __assign(__assign({}, initialValues), data);
            }
            else {
                proposerId = ((_a = currentUser === null || currentUser === void 0 ? void 0 : currentUser.activeOrganization) === null || _a === void 0 ? void 0 : _a.sourceId) ||
                    ((_c = (_b = currentUser === null || currentUser === void 0 ? void 0 : currentUser.userOrganizations) === null || _b === void 0 ? void 0 : _b[0]) === null || _c === void 0 ? void 0 : _c.sourceId);
                formattedData = __assign(__assign({}, data), { legalForm: LegalPersonLegalFormEnum.LEGAL, status: LegalPersonStatusEnum.PROPOSAL, proposerId: proposerId || "" });
            }
            saveMutation.mutate(formattedData);
            return [2 /*return*/];
        });
    }); };
    var handleInvalidSubmit = function (formErrors) {
        openModal(buildPersonInvalidSubmitContent(formatMessage, formErrors));
    };
    var handleCancel = function () {
        var _a;
        history.push(((_a = navigation.legalPersons) === null || _a === void 0 ? void 0 : _a.path) || "/bragency/legal-persons");
    };
    var addIdentifier = function () {
        identifierFields.append({
            type: undefined,
            identifier: "",
        }, { shouldFocus: false });
    };
    var removeIdentifier = function (index) {
        identifierFields.remove(index);
    };
    var addAlternativeName = function () {
        alternativeNameFields.append({ name: "" }, { shouldFocus: false });
    };
    var removeAlternativeName = function (index) {
        alternativeNameFields.remove(index);
    };
    return {
        form: form,
        identifierFields: identifierFields.fields,
        alternativeNameFields: alternativeNameFields.fields,
        isLoading: saveMutation.isLoading,
        error: { isOpen: isOpen, message: message, closeModal: closeModal },
        handlers: {
            onSubmit: handleFormSubmit,
            onInvalid: handleInvalidSubmit,
            onCancel: handleCancel,
            addIdentifier: addIdentifier,
            removeIdentifier: removeIdentifier,
            addAlternativeName: addAlternativeName,
            removeAlternativeName: removeAlternativeName,
        },
        initialValues: initialValues,
        isEdit: isEdit,
    };
};
